"use client";

import React from "react";
import { Facebook, Instagram, Twitter, Share2, MapPin, Phone, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const FooterMenu = React.memo(function FooterMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-accent-foreground transition-colors duration-150 text-sm">
            {item.label}
          </Link>
        </li>)}
    </ul>;
});
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const socialLinks = [{
    name: "Facebook",
    url: "https://www.facebook.com/profile.php?id=61563017601187",
    icon: Facebook
  }, {
    name: "Instagram",
    url: "https://www.instagram.com/guizhoujiahe/",
    icon: Instagram
  }, {
    name: "Twitter",
    url: "https://x.com/guizhoutour",
    icon: Twitter
  }, {
    name: "Pinterest",
    url: "https://www.pinterest.com/jimyang198306/",
    icon: Share2
  }];
  const languages = [{
    code: "en",
    label: "English"
  }, {
    code: "it",
    label: "Italiano"
  }, {
    code: "fr",
    label: "Français"
  }, {
    code: "de",
    label: "Deutsch"
  }, {
    code: "ja",
    label: "日本語"
  }, {
    code: "es",
    label: "Español"
  }];
  return <section id="footer" className="dark bg-background text-foreground pt-16 pb-8 px-6 md:px-8 border-t border-border">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div className="flex flex-col items-start">
            <Link to="/" className="inline-block mb-6">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623046/img/guizhou_voyage_logo_dark.png" alt="Guizhou Voyage logo" className="h-7 w-auto object-contain" loading="lazy" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Specializing in private, authentic Guizhou tours with stunning nature and immersive ethnic minority experiences. Led by travel expert Jim Yang.
            </p>
          </div>

          <div>
            <h3 className="font-default text-accent-foreground font-semibold text-lg mb-6">
              Quick Links
            </h3>
            <MenuProvider menu_id="471">
              <FooterMenu />
            </MenuProvider>
          </div>

          <div>
            <h3 className="font-default text-accent-foreground font-semibold text-lg mb-6">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <Phone className="size-5 text-accent-foreground shrink-0" />
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">Jim Yang</span>
                  <span>+86 15186790899</span>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <Mail className="size-5 text-accent-foreground shrink-0" />
                <Link to="mailto:914491978@qq.com" className="hover:text-accent-foreground transition-colors duration-150">
                  914491978@qq.com
                </Link>
              </li>
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="size-5 text-accent-foreground shrink-0" />
                <span className="leading-relaxed">
                  No. 23, Unit 1, Building 1, Area J, Wulichong Project, Huaguoyuan, Nanming District, Guiyang City, Guizhou Province, China
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-default text-accent-foreground font-semibold text-lg mb-6">
              Follow Us
            </h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.name} data-index={i} to={social.url} newTab className="flex items-center justify-center size-10 rounded-full border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors duration-150" aria-label={social.name}>
                    <Icon className="size-4" />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        <Separator className="my-12 bg-border/50" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-sm text-muted-foreground text-center md:text-left">
            © {currentYear} Guizhou Voyage. All rights reserved.
          </p>

          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:inline-block">
              Language:
            </span>
            <Select defaultValue="en">
              <SelectTrigger className="w-[130px] h-9 bg-transparent border-border text-muted-foreground hover:text-foreground focus:ring-accent-foreground/50">
                <SelectValue placeholder="Select Language" />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang, i) => <SelectItem key={lang.code} data-index={i} value={lang.code}>
                    {lang.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </section>;
}